"use client";

/**
 * MOBILE HERO helper — scale fixed-`baseWidth` content down to exactly fill the
 * parent's width, keeping the height correct. Re-measures on resize and whenever
 * the inner content height changes. Replaces the prototype's fixed `zoom`, which
 * overflowed narrow real-phone viewports (the mobile "kaputt" cause).
 *
 * `shadowPad` / `shadowPadTop` / `shadowPadBottom` / `shadowPadX` (screen px,
 * default 0) reserve breathing room inside the `overflow:hidden` clip box.
 * `shadowPad` sets top and bottom unless overridden. `shadowPadX` shrinks the
 * scaled content horizontally to leave side room for card shadows.
 *
 * `clip={false}` drops the clip box entirely — required when the scaled content
 * carries frame overlays that deliberately hang OUTSIDE the window edge (the
 * Inbox WhatsApp callout at `right: -121`), which the clip box would cut off.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";

export function ResponsiveScale({
  baseWidth,
  borderRadius,
  children,
  clip = true,
  shadowPad = 0,
  shadowPadTop,
  shadowPadBottom,
  shadowPadX = 0,
}: {
  readonly baseWidth: number;
  /** Clips scaled content to rounded corners (needed when a transform sits inside). */
  readonly borderRadius?: number;
  readonly children: ReactNode;
  /** Set false to let overlays paint past the scaled content's edges (see above). */
  readonly clip?: boolean;
  /** Vertical clip padding (top + bottom). Overridden by {@link shadowPadTop} / {@link shadowPadBottom}. */
  readonly shadowPad?: number;
  readonly shadowPadTop?: number;
  readonly shadowPadBottom?: number;
  readonly shadowPadX?: number;
}): ReactNode {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(baseWidth > 0 ? 360 / baseWidth : 0.3);
  const [scaledHeight, setScaledHeight] = useState(0);
  const padTop = shadowPadTop ?? shadowPad;
  const padBottom = shadowPadBottom ?? shadowPad;

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const update = (): void => {
      const w = outer.clientWidth;
      if (w <= 0) return;
      // Fit the content into the width MINUS the horizontal shadow margin.
      const s = Math.max(0, (w - shadowPadX * 2) / baseWidth);
      setScale(s);
      setScaledHeight(inner.scrollHeight * s);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [baseWidth, shadowPadX]);

  return (
    <div
      ref={outerRef}
      style={{
        width: "100%",
        height:
          scaledHeight > 0 ? scaledHeight + padTop + padBottom : undefined,
        overflow: clip ? "hidden" : "visible",
        position: "relative",
        ...(clip && borderRadius !== undefined
          ? { borderRadius, isolation: "isolate" }
          : null),
      }}
    >
      <div
        ref={innerRef}
        style={{
          position: "absolute",
          top: padTop,
          left: shadowPadX,
          width: baseWidth,
          transformOrigin: "top left",
          transform: `scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}
